// Client-side API wrappers & helpers for Google Integration (WP-05).
// Conforms to backend callables in functions/src/google/{oauth,sheets,contacts,calendar}.ts

import { call } from './callable';

export interface GoogleAccountDoc {
  id: string;
  email: string;
  scopes: string[];
  status: 'ok' | 'reauth_required';
  createdAt: number;
  lastSyncAt: number;
  createdBy: string;
  updatedAt: number;
}

export interface GoogleAccountsStats {
  accounts: number;
  sheetsAccess: number;
  calendarAccess: number;
  limitedAccess: number;
}

export interface GoogleSheetDoc {
  id: string;
  title: string;
  url: string;
  accountId: string;
  createdAt: number;
  updatedAt: number;
}

export interface SheetItem {
  id: string;
  title: string;
  url: string;
  modifiedTime?: string;
  source?: 'tracked' | 'drive';
}

export interface ImportContactsResult {
  ok: true;
  imported: number;
  skipped: number;
  errors: string[];
}

export interface GoogleServicesStatus {
  sheets: boolean;
  drive: boolean;
  calendar: boolean;
  contacts: boolean;
}

// ── Callables ─────────────────────────────────────────────────────────────

/** Generates Google OAuth consent URL */
export async function googleAuthUrl(): Promise<{ ok: true; url: string }> {
  const res = await call<{}, { ok: true; url: string }>('googleAuthUrl')({});
  return res.data;
}

/** Lists all connected Google accounts and aggregate service statistics */
export async function googleAccountsList(): Promise<{
  ok: true;
  accounts: GoogleAccountDoc[];
  stats: GoogleAccountsStats;
}> {
  const res = await call<{}, { ok: true; accounts: GoogleAccountDoc[]; stats: GoogleAccountsStats }>(
    'googleAccountsList',
  )({});
  return res.data;
}

/** Unlinks and revokes a connected Google account */
export async function googleUnlink(accountId: string): Promise<{ ok: true }> {
  const res = await call<{ accountId: string }, { ok: true }>('googleUnlink')({ accountId });
  return res.data;
}

/** Tests access token validity and updates lastSyncAt */
export async function googleSyncData(accountId: string): Promise<{ ok: true; lastSyncAt: number }> {
  const res = await call<{ accountId: string }, { ok: true; lastSyncAt: number }>('googleSyncData')({
    accountId,
  });
  return res.data;
}

/** Lists spreadsheets from Google Drive & tracked Firestore docs */
export async function googleSheetsList(accountId: string): Promise<{ ok: true; sheets: SheetItem[] }> {
  const res = await call<{ accountId: string }, { ok: true; sheets: SheetItem[] }>('googleSheetsList')({
    accountId,
  });
  return res.data;
}

/** Creates a new Google Spreadsheet with standard subscriber columns */
export async function googleSheetCreate(
  accountId: string,
  title: string,
): Promise<{ ok: true; sheet: GoogleSheetDoc }> {
  const res = await call<{ accountId: string; title: string }, { ok: true; sheet: GoogleSheetDoc }>(
    'googleSheetCreate',
  )({ accountId, title });
  return res.data;
}

/** Validates access and tracks an existing Google Spreadsheet */
export async function googleSheetAddExisting(
  accountId: string,
  urlOrId: string,
): Promise<{ ok: true; sheet: GoogleSheetDoc }> {
  const res = await call<{ accountId: string; urlOrId: string }, { ok: true; sheet: GoogleSheetDoc }>(
    'googleSheetAddExisting',
  )({ accountId, urlOrId });
  return res.data;
}

/** Fetches row by key column/value from spreadsheet (flow builder / data fetch) */
export async function googleSheetFetch(input: {
  accountId: string;
  sheetId: string;
  tab?: string;
  keyColumn: string;
  keyValue: string;
}): Promise<{ ok: true; row: Record<string, string> | null }> {
  const res = await call<typeof input, { ok: true; row: Record<string, string> | null }>(
    'googleSheetFetch',
  )(input);
  return res.data;
}

/** Imports contacts from Google People API into tenant subscribers */
export async function importGoogleContacts(params: {
  accountId: string;
  integrationId?: string;
  labelIds?: string[];
  listIds?: string[];
}): Promise<ImportContactsResult> {
  const res = await call<typeof params, ImportContactsResult>('importGoogleContacts')(params);
  return res.data;
}

// ── Helpers ───────────────────────────────────────────────────────────────

/**
 * Normalizes a Google Spreadsheet URL or raw ID into the spreadsheet ID.
 * Returns null if the string is empty or invalid.
 */
export function parseSheetId(urlOrId: string): string | null {
  if (!urlOrId) return null;
  const trimmed = urlOrId.trim();
  if (!trimmed) return null;

  const match = trimmed.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
  if (match) return match[1];

  // Raw Google sheet IDs are 15+ alphanumeric characters with dashes or underscores
  if (/^[a-zA-Z0-9-_]{15,}$/.test(trimmed)) {
    return trimmed;
  }

  return null;
}

/**
 * Derives service permissions from an array of Google OAuth scopes.
 */
export function scopeToServices(scopes: string[]): GoogleServicesStatus {
  const joined = (scopes || []).join(' ').toLowerCase();
  return {
    sheets: joined.includes('spreadsheets') || joined.includes('drive'),
    drive: joined.includes('drive'),
    calendar: joined.includes('calendar'),
    contacts: joined.includes('contacts'),
  };
}

/**
 * Formats a timestamp into human-readable relative time (e.g. 'Just now', '5m ago').
 */
export function formatRelativeTime(timestampMs: number | undefined | null): string {
  if (!timestampMs) return 'Never';
  const diffSec = Math.floor((Date.now() - timestampMs) / 1000);
  if (diffSec < 60) return 'Just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return `${diffHour}h ago`;
  const diffDays = Math.floor(diffHour / 24);
  return `${diffDays}d ago`;
}
